import { useMemo, useState } from "react";
import dayjs from "dayjs";

import { API_URL } from "@/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminContractingErrors } from "@/hooks/queries/useAdminContractingErrors";
import type { ContractingFileErrorReport } from "@/types/domain";
import {
  EMPTY_ERROR_FILTERS,
  type ErrorFiltersValue,
} from "@/components/admin/ContractingErrorFilters";
import {
  ContractingReportTable,
  type ReportColumn,
} from "@/components/admin/ContractingReportTable";
import {
  SolutionButtons,
  SolutionResult,
  useSolutionResults,
} from "@/components/admin/ContractingErrorSolutions";

// El motivo del fallo solo existe como texto libre en el log, así que tanto el
// tipo de error como el total de páginas se reconocen acá, sobre lo ya cargado.
const ERROR_KINDS = [
  { value: "pages", label: "Demasiadas páginas", match: /tiene \d+ páginas/ },
  { value: "size", label: "Archivo muy pesado", match: /pesa [\d.]+ MB/ },
  { value: "fewItems", label: "Pocos recursos", match: /Se extrajeron solo/i },
  {
    value: "duplicates",
    label: "Duplicados",
    // El modo presupuesto loguea "filas"; la extracción normal, "descripciones".
    match: /(descripciones|filas) duplicadas/i,
  },
  {
    value: "missingFile",
    label: "Archivo perdido",
    match: /el archivo físico no existe/i,
  },
] as const;

function pagesFromError(lastError: string): number | null {
  const match = lastError.match(/tiene (\d+) páginas/);
  return match ? Number(match[1]) : null;
}

// Archivo y error van juntos: el nombre solo tiene sentido leyendo por qué falló,
// y separarlos dejaba dos columnas angostas peleándose el ancho.
const BASE_COLUMNS: ReportColumn<ContractingFileErrorReport>[] = [
  {
    key: "fileError",
    header: "Archivo y último error",
    className: "min-w-[320px] max-w-[460px]",
    render: (row) => (
      <>
        <FileNameLink row={row} />
        <span className="mt-0.5 line-clamp-2 text-sm text-destructive">
          {row.lastError}
        </span>
      </>
    ),
  },
];

// Abre el archivo que falló para poder revisarlo. El fallback a `/files/` cubre
// los históricos que nunca se subieron a R2.
function FileNameLink({ row }: { row: ContractingFileErrorReport }) {
  const href =
    row.fileUrl ??
    (row.legacyFilePath ? `${API_URL}/files/${row.legacyFilePath}` : null);

  if (!href) {
    return (
      <span
        title="El archivo no está en R2 ni en disco"
        className="line-clamp-1 text-xs text-muted-foreground/60"
      >
        {row.fileName}
      </span>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title="Abrir el archivo que falló"
      className="line-clamp-1 cursor-pointer text-xs text-primary hover:underline"
    >
      {row.fileName}
    </a>
  );
}

export default function AdminContractingErrors() {
  const [filters, setFilters] =
    useState<ErrorFiltersValue>(EMPTY_ERROR_FILTERS);
  const { results, saveResult } = useSolutionResults();
  const [errorKind, setErrorKind] = useState("all");
  const [maxPages, setMaxPages] = useState("");
  const { data = [], isLoading } = useAdminContractingErrors({
    stages: filters.stages,
    publicatedFrom: filters.publicatedFrom || undefined,
    // Fin del día para que el rango incluya la fecha elegida.
    publicatedTo: filters.publicatedTo
      ? dayjs(filters.publicatedTo).endOf("day").toISOString()
      : undefined,
    sortBy: filters.sortBy,
  });

  const rows = useMemo(() => {
    const kind = ERROR_KINDS.find((k) => k.value === errorKind);
    const max = Number(maxPages);
    return data.filter((row) => {
      if (kind && !kind.match.test(row.lastError)) return false;
      if (!maxPages) return true;
      const pages = pagesFromError(row.lastError);
      return pages != null && pages <= max;
    });
  }, [data, errorKind, maxPages]);

  const columns: ReportColumn<ContractingFileErrorReport>[] = [
    ...BASE_COLUMNS,
    {
      key: "solution",
      header: "Solución",
      className: "w-px whitespace-nowrap",
      render: (row) => (
        <SolutionButtons
          fileId={row.fileId}
          nomenclature={row.nomenclature}
          onResult={(result) => saveResult(row.fileId, result)}
        />
      ),
    },
    {
      key: "result",
      header: "Resultado",
      className: "max-w-[260px]",
      render: (row) => <SolutionResult result={results[row.fileId]} />,
    },
  ];

  return (
    <ContractingReportTable
      title="Errores de procesamiento"
      description="Licitaciones cuyos documentos no se pudieron procesar correctamente."
      filters={filters}
      onFiltersChange={setFilters}
      extraFilters={
        <div className="flex items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-muted-foreground">
              Tipo de error
            </Label>
            <Select value={errorKind} onValueChange={setErrorKind}>
              <SelectTrigger className="sm:w-[210px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los errores</SelectItem>
                {ERROR_KINDS.map((kind) => (
                  <SelectItem key={kind.value} value={kind.value}>
                    {kind.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="maxPages" className="text-xs text-muted-foreground">
              Páginas hasta
            </Label>
            <Input
              id="maxPages"
              type="number"
              min={1}
              placeholder="60"
              className="sm:w-[120px]"
              value={maxPages}
              onChange={(e) => setMaxPages(e.target.value)}
            />
          </div>
        </div>
      }
      extraFiltersActive={errorKind !== "all" || !!maxPages}
      rows={rows}
      columns={columns}
      isLoading={isLoading}
      emptyMessage="No hay errores de procesamiento."
    />
  );
}
